<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-use-before-define */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisFilterButton from 'dashboard/components-next/LaisFilterButton.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PendingMarketUpdatesAPI from 'dashboard/api/pendingMarketUpdates';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import PendingMarketOperationsPanel from '../components/PendingMarketOperationsPanel.vue';
import PendingMarketKindTabs from '../components/PendingMarketKindTabs.vue';
import PendingMarketPendentesFilters from '../components/PendingMarketPendentesFilters.vue';
import PendingMarketSummaryCards from '../components/PendingMarketSummaryCards.vue';
import PendingMarketItemCard from '../components/PendingMarketItemCard.vue';
import PendingMarketItemsTable from '../components/PendingMarketItemsTable.vue';
import PendingMarketPagination from '../components/PendingMarketPagination.vue';
import PendingMarketMidasUpdateDialog from '../components/PendingMarketMidasUpdateDialog.vue';
import PendingMarketRefusalDialog from '../components/PendingMarketRefusalDialog.vue';
import PendingMarketBlockOwnerPhoneDialog from '../components/PendingMarketBlockOwnerPhoneDialog.vue';
import PendingMarketSourceLinksDialog from '../components/PendingMarketSourceLinksDialog.vue';
import PendingMarketBlacklistDialog from '../components/PendingMarketBlacklistDialog.vue';
import PendingMarketLaudoRegistrationDialog from '../components/PendingMarketLaudoRegistrationDialog.vue';
import {
  HIDDEN_PENDING_STATUSES_PARAM,
  INITIAL_FILTERS,
  LAUDO_STATUS_POLL_INTERVAL_MS,
  LAUDO_STATUS_POLL_MAX_ATTEMPTS,
  PAGE_SIZE,
  PENDING_STATUSES,
  REFUSAL_OPTIONS_BY_KIND,
  SELECT_CLASS as selectClass,
  SENT_STATUSES,
  SKIPPED_STATUSES,
  SURFACE_IDS,
  TEXTAREA_CLASS as textareaClass,
} from '../utils/pendentesConstants';
import {
  canBlockOwnerPhone,
  canUpdateMidas,
  formatDate,
  formatMoney,
  isHiddenPendingStatus,
  isLaudoRegisteredStatus,
  isRefusalActionVisible,
  isRegisterActionVisible,
  kindHint,
  normalizePendingKind,
  number,
  propertyCodeForMidas,
  propertySubtitle,
  propertyTitle,
} from '../utils/pendentesFormatters';
import {
  hiddenSourceLinksCount,
  itemSourceLinks,
  rootUrl,
} from '../utils/pendentesSourceLinks';
import { usePendentesBlacklist } from '../composables/usePendentesBlacklist';

const emit = defineEmits(['mercadoTabState']);

const route = useRoute();
const router = useRouter();

const currentUser = useMapGetter('getCurrentUser');
const routeQueryValue = key => {
  const value = route.query[key];
  return Array.isArray(value) ? value[0] || '' : value || '';
};
const initialFiltersFromRoute = () => ({
  ...INITIAL_FILTERS,
  sourceKind: routeQueryValue('sourceKind'),
  updatedSince: routeQueryValue('updatedSince') || routeQueryValue('desde'),
  updatedUntil: routeQueryValue('updatedUntil') || routeQueryValue('ate'),
});
const tipos = ref([]);
const activeKind = ref('');
const columns = ref([]);
const items = ref([]);
const pageMeta = ref({
  page: 1,
  limit: PAGE_SIZE,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrevious: false,
});
const isLoadingTypes = ref(false);
const isLoadingItems = ref(false);
const isLoadingOperations = ref(false);
const showFilters = ref(false);
const source = ref('');
const errorMessage = ref('');
const operationErrorMessage = ref('');
const operationSnapshot = ref(null);
const filters = ref(initialFiltersFromRoute());
const surfaceFromRoute = () => {
  const surface = String(route.query.surface || '');
  return SURFACE_IDS.has(surface) ? surface : 'items';
};
const activeSurface = ref(surfaceFromRoute());
const midasDialogRef = ref(null);
const midasTargetItem = ref(null);
const updatingMidasItemId = ref('');
const registerDialogRef = ref(null);
const registeringLaudoItemId = ref('');
const sourceLinksDialogRef = ref(null);
const sourceLinksDialogItem = ref(null);
const refusalDialogRef = ref(null);
const refusalTargetItem = ref(null);
const refusalStatus = ref('');
const refusalJustification = ref('');
const savingRefusalItemId = ref('');
const blockOwnerDialogRef = ref(null);
const blockOwnerTargetItem = ref(null);
const blockOwnerReason = ref('');
const savingBlockOwnerItemId = ref('');
const blacklistDialogRef = ref(null);
const laudoStatusPollTimers = new Map();
const laudoStatusPollNotifyIds = new Set();

const currentPublisherActorPayload = computed(() => {
  const user = currentUser.value || {};
  return {
    actorName:
      user.available_name ||
      user.availableName ||
      user.display_name ||
      user.displayName ||
      user.name ||
      '',
    actorEmail: user.email || '',
  };
});

const {
  blacklistEntries,
  blacklistSearch,
  blacklistPhone,
  blacklistReason,
  isLoadingBlacklist,
  savingBlacklist,
  removingBlacklistId,
  isSavingBlacklist,
  canCreateBlacklistEntry,
  loadBlacklist,
  createBlacklistEntry,
  removeBlacklistEntry,
  closeBlacklistDialog,
} = usePendentesBlacklist({
  actorPayload: currentPublisherActorPayload,
  onChanged: () => refreshAll(),
});

const activeTipo = computed(
  () => tipos.value.find(tipo => tipo.kind === activeKind.value) || null
);
const statusOptions = computed(() => activeTipo.value?.statuses || []);
const isLoading = computed(() => isLoadingTypes.value || isLoadingItems.value);
const showMidasActions = computed(() => activeKind.value === 'price_reduction');
const showRegisterActions = computed(
  () => activeKind.value === 'outside_house'
);
const showRowActions = computed(
  () => showMidasActions.value || showRegisterActions.value
);
const pageStart = computed(() =>
  pageMeta.value.total ? (pageMeta.value.page - 1) * PAGE_SIZE + 1 : 0
);
const pageEnd = computed(() =>
  Math.min(pageMeta.value.page * PAGE_SIZE, pageMeta.value.total || 0)
);
const lastUpdateLabel = computed(() =>
  formatDate(activeTipo.value?.lastProcessedAt)
);
const isUpdatingMidas = computed(() => Boolean(updatingMidasItemId.value));
const isSavingRefusal = computed(() => Boolean(savingRefusalItemId.value));
const isSavingBlockOwner = computed(() =>
  Boolean(savingBlockOwnerItemId.value)
);
const midasTargetTitle = computed(() =>
  midasTargetItem.value ? propertyTitle(midasTargetItem.value) : 'imóvel'
);
const midasTargetCode = computed(
  () => propertyCodeForMidas(midasTargetItem.value) || '-'
);
const midasTargetNewPrice = computed(() =>
  formatMoney(midasTargetItem.value?.competitorPrice)
);
const midasTargetCurrentPrice = computed(() =>
  formatMoney(midasTargetItem.value?.housePrice)
);
const activeFiltersCount = computed(
  () =>
    [
      filters.value.q,
      filters.value.status,
      filters.value.sentState,
      filters.value.sourceKind,
      filters.value.neighbourhood,
      filters.value.city,
      filters.value.updatedSince,
      filters.value.updatedUntil,
    ].filter(Boolean).length
);
const activeFilterChips = computed(() => {
  const chips = [];
  if (filters.value.q) {
    chips.push({ key: 'q', label: `Busca: ${filters.value.q}` });
  }
  if (filters.value.status) {
    const status = statusOptions.value.find(
      item => item.status === filters.value.status
    );
    chips.push({
      key: 'status',
      label: `Status: ${status?.label || filters.value.status}`,
    });
  }
  if (filters.value.sentState) {
    chips.push({
      key: 'sentState',
      label:
        filters.value.sentState === 'sent'
          ? 'Com envio'
          : 'Sem envio registrado',
    });
  }
  if (filters.value.sourceKind) {
    chips.push({
      key: 'sourceKind',
      label: `Origem: ${filters.value.sourceKind}`,
    });
  }
  if (filters.value.neighbourhood) {
    chips.push({
      key: 'neighbourhood',
      label: `Bairro: ${filters.value.neighbourhood}`,
    });
  }
  if (filters.value.city) {
    chips.push({ key: 'city', label: `Cidade: ${filters.value.city}` });
  }
  if (filters.value.updatedSince || filters.value.updatedUntil) {
    chips.push({
      key: 'dateRange',
      label: `Período: ${filters.value.updatedSince || 'inicio'} - ${
        filters.value.updatedUntil || 'hoje'
      }`,
    });
  }
  return chips;
});
const refusalOptions = computed(
  () => REFUSAL_OPTIONS_BY_KIND[refusalTargetItem.value?.kind] || []
);
const refusalTargetTitle = computed(() =>
  refusalTargetItem.value ? propertyTitle(refusalTargetItem.value) : 'imóvel'
);
const canConfirmRefusal = computed(
  () =>
    Boolean(refusalTargetItem.value) &&
    Boolean(refusalStatus.value) &&
    refusalJustification.value.trim().length >= 5 &&
    !isSavingRefusal.value
);
const blockOwnerTargetTitle = computed(() =>
  blockOwnerTargetItem.value
    ? propertyTitle(blockOwnerTargetItem.value)
    : 'telefone'
);
const blockOwnerTargetPhone = computed(
  () => blockOwnerTargetItem.value?.ownerPhone || ''
);
const canConfirmBlockOwner = computed(
  () =>
    Boolean(blockOwnerTargetItem.value?.ownerPhone) &&
    blockOwnerReason.value.trim().length >= 5 &&
    !isSavingBlockOwner.value
);

function countStatuses(predicate) {
  return statusOptions.value
    .filter(item => predicate(item.status))
    .reduce((sum, item) => sum + Number(item.count || 0), 0);
}

function ensureVisibleStatusFilter() {
  if (!filters.value.status) return;
  if (isHiddenPendingStatus(filters.value.status)) {
    filters.value.status = '';
    return;
  }
  if (
    activeTipo.value &&
    !statusOptions.value.some(item => item.status === filters.value.status)
  ) {
    filters.value.status = '';
  }
}

const detectedCount = computed(() =>
  countStatuses(status => PENDING_STATUSES.includes(status))
);
const sentCount = computed(() =>
  countStatuses(status => SENT_STATUSES.includes(status))
);
const skippedCount = computed(() =>
  countStatuses(
    status =>
      !isHiddenPendingStatus(status) &&
      (status.startsWith('skipped_') || SKIPPED_STATUSES.includes(status))
  )
);

const summaryCards = computed(() => [
  {
    key: 'pending',
    label: 'Pendentes',
    value: detectedCount.value,
    icon: 'i-lucide-clock-3',
    tone: 'bg-yellow-50 text-yellow-800 ring-yellow-100',
  },
  {
    key: 'sent',
    label: 'Enviados',
    value: sentCount.value,
    icon: 'i-lucide-send',
    tone: 'bg-green-50 text-green-700 ring-green-100',
  },
  {
    key: 'skipped',
    label: 'Ignorados',
    value: skippedCount.value,
    icon: 'i-lucide-archive-x',
    tone: 'bg-slate-100 text-slate-700 ring-slate-200',
  },
]);
const surfaceTabs = computed(() => [
  {
    id: 'items',
    label: 'Itens',
    icon: 'i-lucide-list-checks',
    badge: pageMeta.value.total,
  },
  {
    id: 'operation',
    label: 'Operação',
    icon: 'i-lucide-activity',
    badge: errorMessage.value ? '!' : '',
  },
]);
const activeMercadoTabKey = computed(() =>
  activeSurface.value === 'operation' ? 'laudos' : 'pendentes'
);
const activeMercadoTabErrorMessage = computed(() =>
  activeSurface.value === 'operation'
    ? operationErrorMessage.value || errorMessage.value
    : errorMessage.value
);
const isActiveMercadoTabLoading = computed(() =>
  activeSurface.value === 'operation'
    ? isLoadingOperations.value || isLoading.value
    : isLoading.value
);
const mercadoTabStatus = computed(() => {
  if (isActiveMercadoTabLoading.value) return 'loading';
  return activeMercadoTabErrorMessage.value ? 'unavailable' : 'ready';
});
const mercadoTabState = computed(() => ({
  key: activeMercadoTabKey.value,
  status: mercadoTabStatus.value,
  message: activeMercadoTabErrorMessage.value,
}));

const sourceLinksDialogLinks = computed(() =>
  itemSourceLinks(sourceLinksDialogItem.value)
);

const sourceLinksDialogTitle = computed(() =>
  sourceLinksDialogItem.value ? propertyTitle(sourceLinksDialogItem.value) : ''
);

const sourceLinksDialogSubtitle = computed(() =>
  sourceLinksDialogItem.value
    ? propertySubtitle(sourceLinksDialogItem.value)
    : ''
);

function openSourceLinksDialog(item) {
  if (!hiddenSourceLinksCount(item)) return;
  sourceLinksDialogItem.value = item;
  sourceLinksDialogRef.value?.open();
}

function closeSourceLinksDialog() {
  sourceLinksDialogItem.value = null;
}

function updateLocalItem(nextItem) {
  if (!nextItem?.id) return;
  items.value = items.value.map(item =>
    String(item.id) === String(nextItem.id) ? { ...item, ...nextItem } : item
  );
}

function clearAllLaudoStatusPolling() {
  laudoStatusPollTimers.forEach(timer => clearTimeout(timer));
  laudoStatusPollTimers.clear();
  laudoStatusPollNotifyIds.clear();
}

function startLaudoStatusPolling(itemId, { attempt = 0, notify = false } = {}) {
  const key = String(itemId || '');
  if (!key) return;
  if (notify) laudoStatusPollNotifyIds.add(key);
  if (laudoStatusPollTimers.has(key)) return;

  const timer = setTimeout(async () => {
    laudoStatusPollTimers.delete(key);
    try {
      const { data } = await PendingMarketUpdatesAPI.item(key);
      const nextItem = data?.item;
      if (nextItem) updateLocalItem(nextItem);

      if (nextItem?.status === 'midas_laudo_registering') {
        if (attempt + 1 < LAUDO_STATUS_POLL_MAX_ATTEMPTS) {
          startLaudoStatusPolling(key, { attempt: attempt + 1 });
        }
        return;
      }

      if (nextItem) {
        await refreshAll();
        if (laudoStatusPollNotifyIds.has(key)) {
          if (isLaudoRegisteredStatus(nextItem.status)) {
            useAlert('Cadastro de laudo concluído no Midas.');
          } else if (nextItem.status === 'midas_laudo_register_failed') {
            useAlert(nextItem.error || 'Cadastro de laudo falhou no Midas.');
          }
        }
      }
      laudoStatusPollNotifyIds.delete(key);
    } catch (_error) {
      if (attempt + 1 < LAUDO_STATUS_POLL_MAX_ATTEMPTS) {
        startLaudoStatusPolling(key, { attempt: attempt + 1 });
      }
    }
  }, LAUDO_STATUS_POLL_INTERVAL_MS);

  laudoStatusPollTimers.set(key, timer);
}

function openRefusalDialog(item) {
  if (!isRefusalActionVisible(item)) return;
  refusalTargetItem.value = item;
  refusalStatus.value = REFUSAL_OPTIONS_BY_KIND[item.kind]?.[0]?.value || '';
  refusalJustification.value = '';
  refusalDialogRef.value?.open();
}

function closeRefusalDialog() {
  if (isSavingRefusal.value) return;
  refusalTargetItem.value = null;
  refusalStatus.value = '';
  refusalJustification.value = '';
}

function openBlockOwnerDialog(item) {
  if (!canBlockOwnerPhone(item)) return;
  blockOwnerTargetItem.value = item;
  blockOwnerReason.value = '';
  blockOwnerDialogRef.value?.open();
}

function closeBlockOwnerDialog() {
  if (isSavingBlockOwner.value) return;
  blockOwnerTargetItem.value = null;
  blockOwnerReason.value = '';
}

async function openBlacklistDialog() {
  blacklistDialogRef.value?.open();
  await loadBlacklist();
}

function openMidasDialog(item) {
  if (!canUpdateMidas(item)) {
    useAlert(
      'Não foi possível preparar a atualização: falta código do imóvel ou valor novo.'
    );
    return;
  }
  midasTargetItem.value = item;
  midasDialogRef.value?.open();
}

function closeMidasDialog() {
  if (isUpdatingMidas.value) return;
  midasTargetItem.value = null;
}

async function confirmMidasUpdate() {
  if (!midasTargetItem.value) return;

  const item = midasTargetItem.value;
  updatingMidasItemId.value = String(item.id);
  try {
    const { data } = await PendingMarketUpdatesAPI.updateMidas(
      item.id,
      currentPublisherActorPayload.value
    );
    const message =
      data?.message ||
      `Atualizacao enviada para o Midas: ${formatMoney(item.competitorPrice)}.`;
    useAlert(message);
    midasDialogRef.value?.close();
    midasTargetItem.value = null;
    await refreshAll();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.response?.data?.details?.detail ||
        'Nao foi possivel atualizar o valor no Midas.'
    );
  } finally {
    updatingMidasItemId.value = '';
  }
}

function openRegisterDialog(item) {
  if (!isRegisterActionVisible(item)) return;
  registerDialogRef.value?.open(item);
}

async function handleLaudoRegistered({ item, data }) {
  await refreshAll();
  if (data?.status === 'midas_laudo_registering') {
    startLaudoStatusPolling(item.id, { notify: true });
  }
}

async function confirmRefusal() {
  if (!canConfirmRefusal.value || !refusalTargetItem.value) return;
  const item = refusalTargetItem.value;
  savingRefusalItemId.value = String(item.id);
  try {
    const { data } = await PendingMarketUpdatesAPI.refuse(item.id, {
      ...currentPublisherActorPayload.value,
      status: refusalStatus.value,
      justification: refusalJustification.value.trim(),
    });
    useAlert(data?.message || 'Status de negativa salvo.');
    refusalDialogRef.value?.close();
    closeRefusalDialog();
    await refreshAll();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.response?.data?.details?.detail ||
        'Nao foi possivel salvar a negativa.'
    );
  } finally {
    savingRefusalItemId.value = '';
  }
}

async function confirmBlockOwnerPhone() {
  if (!canConfirmBlockOwner.value || !blockOwnerTargetItem.value) return;
  const item = blockOwnerTargetItem.value;
  savingBlockOwnerItemId.value = String(item.id);
  try {
    const { data } = await PendingMarketUpdatesAPI.blockOwnerPhone(item.id, {
      ...currentPublisherActorPayload.value,
      phone: item.ownerPhone,
      reason: blockOwnerReason.value.trim(),
    });
    useAlert(data?.message || 'Telefone adicionado à blacklist.');
    blockOwnerDialogRef.value?.close();
    closeBlockOwnerDialog();
    await refreshAll();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.response?.data?.details?.detail ||
        'Não foi possível adicionar o telefone à blacklist.'
    );
  } finally {
    savingBlockOwnerItemId.value = '';
  }
}

async function loadTypes() {
  isLoadingTypes.value = true;
  errorMessage.value = '';
  try {
    const { data } = await PendingMarketUpdatesAPI.kinds();
    tipos.value = (data.items || []).map(normalizePendingKind);
    source.value = data.source || '';
    if (!tipos.value.some(tipo => tipo.kind === activeKind.value)) {
      activeKind.value = tipos.value[0]?.kind || '';
    }
    ensureVisibleStatusFilter();
  } catch (error) {
    errorMessage.value =
      error.response?.data?.error ||
      'Nao foi possivel carregar os tipos de pendentes.';
    useAlert(errorMessage.value);
  } finally {
    isLoadingTypes.value = false;
  }
}

async function loadItems() {
  if (!activeKind.value) return;

  isLoadingItems.value = true;
  errorMessage.value = '';
  try {
    const { data } = await PendingMarketUpdatesAPI.items({
      kind: activeKind.value,
      page: pageMeta.value.page,
      pageSize: PAGE_SIZE,
      excludeStatuses: HIDDEN_PENDING_STATUSES_PARAM,
      ...filters.value,
    });
    columns.value = data.columns || [];
    items.value = data.items || [];
    pageMeta.value = data.page || pageMeta.value;
    source.value = data.source || source.value;
  } catch (error) {
    items.value = [];
    columns.value = [];
    errorMessage.value =
      error.response?.data?.error || 'Nao foi possivel carregar os pendentes.';
    useAlert(errorMessage.value);
  } finally {
    isLoadingItems.value = false;
  }
}

async function loadOperations() {
  isLoadingOperations.value = true;
  operationErrorMessage.value = '';
  try {
    const { data } = await PendingMarketUpdatesAPI.operations({
      kind: activeKind.value || 'all',
    });
    operationSnapshot.value = data || null;
  } catch (error) {
    operationSnapshot.value = null;
    operationErrorMessage.value =
      error.response?.data?.error ||
      'Nao foi possivel carregar o cockpit operacional.';
  } finally {
    isLoadingOperations.value = false;
  }
}

async function refreshAll() {
  await loadTypes();
  await loadItems();
  await loadOperations();
}

async function selectKind(kind) {
  if (activeKind.value === kind) return;
  activeKind.value = kind;
  pageMeta.value.page = 1;
  filters.value.status = '';
  await loadItems();
}

function applyFilters() {
  pageMeta.value.page = 1;
  syncMercadoSharedQuery();
  loadItems();
}

function resetFilters() {
  filters.value = { ...INITIAL_FILTERS };
  pageMeta.value.page = 1;
  syncMercadoSharedQuery();
  loadItems();
}

function removeFilter(key) {
  if (key === 'dateRange') {
    filters.value.updatedSince = '';
    filters.value.updatedUntil = '';
  } else {
    filters.value[key] = '';
  }
  applyFilters();
}

function sortByColumn(column) {
  if (!column.sortBy) return;
  if (filters.value.sortBy === column.sortBy) {
    filters.value.sortDirection =
      filters.value.sortDirection === 'asc' ? 'desc' : 'asc';
  } else {
    filters.value.sortBy = column.sortBy;
    filters.value.sortDirection = 'asc';
  }
  pageMeta.value.page = 1;
  loadItems();
}

function goToPage(nextPage) {
  if (
    nextPage < 1 ||
    nextPage > pageMeta.value.totalPages ||
    isLoadingItems.value
  ) {
    return;
  }
  pageMeta.value.page = nextPage;
  loadItems();
}

function selectSurface(surfaceId) {
  activeSurface.value = surfaceId;
  const query = { ...route.query };
  if (surfaceId === 'items') {
    delete query.surface;
  } else {
    query.surface = surfaceId;
  }
  router.replace({ name: route.name, params: route.params, query });
  if (surfaceId === 'operation') loadOperations();
}

function setRouteQueryValue(query, key, value) {
  const normalized = String(value || '').trim();
  if (normalized) {
    query[key] = normalized;
  } else {
    delete query[key];
  }
}

function normalizedQuerySnapshot(query) {
  return Object.keys(query)
    .sort()
    .map(key => [key, Array(query[key]).flat().join('|')]);
}

function replaceRouteQueryIfChanged(query) {
  if (
    JSON.stringify(normalizedQuerySnapshot(query)) ===
    JSON.stringify(normalizedQuerySnapshot(route.query))
  ) {
    return;
  }
  router.replace({ name: route.name, params: route.params, query });
}

function syncMercadoSharedQuery() {
  const query = { ...route.query };
  setRouteQueryValue(query, 'sourceKind', filters.value.sourceKind);
  setRouteQueryValue(query, 'updatedSince', filters.value.updatedSince);
  setRouteQueryValue(query, 'updatedUntil', filters.value.updatedUntil);
  delete query.desde;
  delete query.ate;
  replaceRouteQueryIfChanged(query);
}

function applyRouteFilters() {
  const routeFilters = initialFiltersFromRoute();
  const routeFilterKeys = ['sourceKind', 'updatedSince', 'updatedUntil'];
  const hasChanged = routeFilterKeys.some(
    key => filters.value[key] !== routeFilters[key]
  );
  if (!hasChanged) return;

  filters.value = {
    ...filters.value,
    ...routeFilterKeys.reduce((nextFilters, key) => {
      nextFilters[key] = routeFilters[key];
      return nextFilters;
    }, {}),
  };
  pageMeta.value.page = 1;
  loadItems();
}

watch(
  () => route.query.surface,
  () => {
    activeSurface.value = surfaceFromRoute();
  }
);
watch(
  () => [
    route.query.sourceKind,
    route.query.updatedSince,
    route.query.updatedUntil,
    route.query.desde,
    route.query.ate,
  ],
  applyRouteFilters
);
watch(mercadoTabState, state => emit('mercadoTabState', state), {
  immediate: true,
});
watch(items, rows => {
  rows
    .filter(item => item?.status === 'midas_laudo_registering')
    .forEach(item => startLaudoStatusPolling(item.id));
});

onMounted(async () => {
  await loadTypes();
  await loadItems();
  if (activeSurface.value === 'operation') await loadOperations();
});

onBeforeUnmount(() => {
  clearAllLaudoStatusPolling();
});
</script>

<template>
  <section
    class="block h-full w-full min-w-0 max-w-full overflow-y-auto overflow-x-hidden overscroll-contain bg-[#f7f8fb] font-redHatText [contain:paint]"
  >
    <LaisHeader
      class="shrink-0"
      title="Pendentes"
      helper="Revise os imóveis classificados pela automação e execute as ações no Midas sem sair da Imudei."
      :title-badge="source === 'database' ? 'Dados ao vivo' : ''"
    >
      <template #actions>
        <button
          type="button"
          class="inline-flex h-9 items-center gap-2 rounded-lais border border-lais-border bg-white px-3 text-[12px] font-bold uppercase text-lais-text transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
          @click="openBlacklistDialog"
        >
          <span class="i-lucide-ban size-3.5" aria-hidden="true" />
          Blacklist
        </button>
        <LaisFilterButton
          :active="showFilters || activeFiltersCount > 0"
          @click="showFilters = !showFilters"
        >
          Filtros
          <span
            v-if="activeFiltersCount"
            class="ml-1 inline-flex min-w-5 justify-center rounded-full bg-lais-primary px-1.5 text-[11px] text-white"
          >
            {{ activeFiltersCount }}
          </span>
        </LaisFilterButton>
        <LaisPrimaryButton
          icon="i-lucide-refresh-cw"
          variant="ghost"
          :is-loading="isLoading"
          @click="refreshAll"
        >
          Atualizar
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <PendingMarketKindTabs
      :tipos="tipos"
      :active-kind="activeKind"
      @select="selectKind"
    />

    <PendingMarketPendentesFilters
      v-model:filters="filters"
      :status-options="statusOptions"
      :active-filters-count="activeFiltersCount"
      :active-filter-chips="activeFilterChips"
      :show-filters="showFilters"
      @apply="applyFilters"
      @reset="resetFilters"
      @remove-filter="removeFilter"
    />

    <main
      class="w-full min-w-0 max-w-full bg-lais-bg-page px-4 pb-24 pt-4 sm:px-6 md:pb-8 lg:px-10"
    >
      <nav
        class="mb-4 flex gap-1.5 overflow-x-auto border-b border-lais-border-soft pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label="Visões de pendentes"
        role="tablist"
      >
        <button
          v-for="tab in surfaceTabs"
          :key="tab.id"
          type="button"
          role="tab"
          class="inline-flex h-10 shrink-0 items-center gap-2 rounded-lais px-3.5 text-[13px] font-semibold outline-none transition focus-visible:shadow-lais-focus"
          :class="
            activeSurface === tab.id
              ? 'bg-lais-primary text-white shadow-lais-card'
              : 'bg-white text-lais-text-muted hover:bg-lais-primary-50 hover:text-lais-primary'
          "
          :aria-selected="activeSurface === tab.id ? 'true' : 'false'"
          @click="selectSurface(tab.id)"
        >
          <span class="size-4" :class="tab.icon" aria-hidden="true" />
          {{ tab.label }}
          <span
            v-if="tab.badge"
            class="grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold tabular-nums"
            :class="
              activeSurface === tab.id
                ? 'bg-white/20 text-white'
                : 'bg-lais-bg-soft text-lais-text-muted'
            "
          >
            {{ tab.badge }}
          </span>
        </button>
      </nav>

      <PendingMarketOperationsPanel
        v-if="activeSurface === 'operation'"
        :tipos="tipos"
        :active-kind="activeKind"
        :items="items"
        :page-meta="pageMeta"
        :source="source"
        :operation-snapshot="operationSnapshot"
        :error-message="operationErrorMessage || errorMessage"
        :loading="isLoading || isLoadingOperations"
        @retry="refreshAll"
      />

      <!-- prettier-ignore-start -->
      <template v-else>
        <PendingMarketSummaryCards :cards="summaryCards" />

        <div
          class="mt-4 flex flex-col gap-2 rounded-lais border border-lais-border bg-white px-4 py-3 shadow-lais-card sm:flex-row sm:items-center sm:justify-between lg:rounded-none lg:border-0 lg:bg-transparent lg:px-0 lg:shadow-none"
        >
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-[16px] font-semibold text-lais-text">
                {{ activeTipo?.label || 'Registros' }}
              </h2>
              <span
                class="inline-flex items-center gap-1 rounded-full border border-lais-border bg-lais-bg-page px-2 py-0.5 text-[11px] font-semibold text-lais-text-muted"
              >
                <span class="i-lucide-database size-3" aria-hidden="true" />
                {{
                  source === 'database' ? 'Ao vivo' : source || 'Fonte externa'
                }}
              </span>
            </div>
            <p class="mt-0.5 text-[12px] text-lais-text-muted">
              Exibindo {{ number(pageStart) }}-{{ number(pageEnd) }} de
              {{ number(pageMeta.total) }} registros · última atualização:
              {{ lastUpdateLabel }}
            </p>
          </div>
          <p class="text-[13px] font-semibold text-lais-text-muted">
            {{ kindHint(activeKind) }}
          </p>
        </div>

        <div
          v-if="errorMessage"
          class="mt-4 rounded-lais border border-red-100 bg-red-50 px-4 py-3 text-[13px] font-semibold text-red-700"
        >
          {{ errorMessage }}
        </div>

        <div v-if="!tipos.length && !isLoadingTypes" class="mt-4">
          <LaisCard>
            <div class="grid place-items-center py-12 text-center">
              <span
                class="i-lucide-inbox mx-auto size-9 text-lais-text-muted"
                aria-hidden="true"
              />
              <h2 class="mt-3 text-[16px] font-semibold text-lais-text">
                Nenhum pendente encontrado
              </h2>
              <p class="mt-1 max-w-sm text-[13px] text-lais-text-muted">
                Quando novos laudos forem processados, eles aparecem aqui por
                tipo de mensagem.
              </p>
            </div>
          </LaisCard>
        </div>

        <template v-else>
          <div
            class="mt-4 grid min-w-0 gap-3 overflow-hidden sm:grid-cols-2 lg:hidden"
          >
            <template v-if="isLoadingItems">
              <div
                v-for="index in 4"
                :key="index"
                class="animate-pulse rounded-lais border border-lais-border bg-white p-4 shadow-lais-card"
              >
                <div class="flex gap-3">
                  <div class="size-12 rounded-full bg-lais-bg-page" />
                  <div class="min-w-0 flex-1 space-y-2">
                    <div class="h-4 w-3/4 rounded bg-lais-bg-page" />
                    <div class="h-3 w-1/2 rounded bg-lais-bg-page" />
                  </div>
                </div>
                <div class="mt-4 grid grid-cols-2 gap-2">
                  <div class="h-12 rounded-lais bg-lais-bg-page" />
                  <div class="h-12 rounded-lais bg-lais-bg-page" />
                </div>
              </div>
            </template>

            <LaisCard v-else-if="!items.length">
              <div class="grid place-items-center py-10 text-center">
                <span
                  class="i-lucide-search-x mx-auto size-8 text-lais-text-muted"
                  aria-hidden="true"
                />
                <h2 class="mt-3 text-[15px] font-semibold text-lais-text">
                  Nada com esses filtros
                </h2>
                <button
                  type="button"
                  class="mt-3 text-[13px] font-semibold text-lais-primary underline"
                  @click="resetFilters"
                >
                  Limpar filtros
                </button>
              </div>
            </LaisCard>

            <template v-else>
              <PendingMarketItemCard
                v-for="item in items"
                :key="item.id"
                :item="item"
                :columns="columns"
                :updating-midas-item-id="updatingMidasItemId"
                :registering-laudo-item-id="registeringLaudoItemId"
                :saving-refusal-item-id="savingRefusalItemId"
                @midas="openMidasDialog"
                @register="openRegisterDialog"
                @refuse="openRefusalDialog"
                @block-owner-phone="openBlockOwnerDialog"
                @open-source-links="openSourceLinksDialog"
              />
            </template>
          </div>

          <PendingMarketItemsTable
            :items="items"
            :columns="columns"
            :is-loading-items="isLoadingItems"
            :show-row-actions="showRowActions"
            :sort-by="filters.sortBy"
            :sort-direction="filters.sortDirection"
            :updating-midas-item-id="updatingMidasItemId"
            :registering-laudo-item-id="registeringLaudoItemId"
            :saving-refusal-item-id="savingRefusalItemId"
            @sort="sortByColumn"
            @midas="openMidasDialog"
            @register="openRegisterDialog"
            @refuse="openRefusalDialog"
            @block-owner-phone="openBlockOwnerDialog"
            @open-source-links="openSourceLinksDialog"
          />

          <PendingMarketPagination
            :page-meta="pageMeta"
            :is-loading-items="isLoadingItems"
            @go-to-page="goToPage"
          />
        </template>
      </template>
      <!-- prettier-ignore-end -->
    </main>

    <PendingMarketSourceLinksDialog
      ref="sourceLinksDialogRef"
      :item="sourceLinksDialogItem"
      :title="sourceLinksDialogTitle"
      :subtitle="sourceLinksDialogSubtitle"
      :links="sourceLinksDialogLinks"
      :root-url="rootUrl"
      @close="closeSourceLinksDialog"
    />

    <PendingMarketBlockOwnerPhoneDialog
      ref="blockOwnerDialogRef"
      v-model:reason="blockOwnerReason"
      :target-phone="blockOwnerTargetPhone"
      :target-title="blockOwnerTargetTitle"
      :is-loading="isSavingBlockOwner"
      :disable-confirm="!canConfirmBlockOwner"
      :textarea-class="textareaClass"
      @confirm="confirmBlockOwnerPhone"
      @close="closeBlockOwnerDialog"
    />

    <PendingMarketBlacklistDialog
      ref="blacklistDialogRef"
      v-model:search="blacklistSearch"
      v-model:phone="blacklistPhone"
      v-model:reason="blacklistReason"
      :entries="blacklistEntries"
      :is-loading-entries="isLoadingBlacklist"
      :is-creating="savingBlacklist"
      :is-saving="isSavingBlacklist"
      :removing-entry-id="removingBlacklistId"
      :can-create="canCreateBlacklistEntry"
      @search="loadBlacklist"
      @create="createBlacklistEntry"
      @remove="removeBlacklistEntry"
      @close="closeBlacklistDialog"
    />

    <PendingMarketRefusalDialog
      ref="refusalDialogRef"
      v-model:status="refusalStatus"
      v-model:justification="refusalJustification"
      :target-title="refusalTargetTitle"
      :options="refusalOptions"
      :is-loading="isSavingRefusal"
      :disable-confirm="!canConfirmRefusal"
      :select-class="selectClass"
      :textarea-class="textareaClass"
      @confirm="confirmRefusal"
      @close="closeRefusalDialog"
    />

    <PendingMarketMidasUpdateDialog
      ref="midasDialogRef"
      :title="midasTargetTitle"
      :code="midasTargetCode"
      :current-price="midasTargetCurrentPrice"
      :new-price="midasTargetNewPrice"
      :is-loading="isUpdatingMidas"
      :disable-confirm="!midasTargetItem || isUpdatingMidas"
      @confirm="confirmMidasUpdate"
      @close="closeMidasDialog"
    />

    <PendingMarketLaudoRegistrationDialog
      ref="registerDialogRef"
      v-model:registering-item-id="registeringLaudoItemId"
      :current-user="currentUser"
      :actor-payload="currentPublisherActorPayload"
      :after-register="handleLaudoRegistered"
    />
  </section>
</template>
